
import { Component, Vue, Watch } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import DetailsList from "@/components/DetailsList/DetailsList.vue";
import { encodeUrlParams, someValueInFormIsEmpty } from "@/utils/methods";
import {
  API_TRoleTemplate,
  convertJobTemplatesToSelectCaseCreation,
} from "@/models/role_templates";
import { TCandidateInvite } from "@/models/cases";
import { SMCases, SMRoles, SMQuestionnaire, SMUser } from "@/store";
import { REmployerHomeNew } from "@/pages/Layout/views/Employer/views/HomeNew/REmployerHome";
import { CFormField } from "@/models/form_cards";
import { REs } from "@/utils/REs";

const FORM_MODEL = {
  candidate_first_name: "",
  candidate_last_name: "",
  candidate_email: "",
  role_template_id: "",
};

const ICONS = {
  add: "add",
};

Component.registerHooks(["beforeRouteEnter", "beforeRouteLeave"]);

@Component({
  components: {
    DetailsList,
    // ActionContainerCard: () => import("@/components/ActionContainer/ActionContainerCard.vue"),
    SLRoleTemplatePopup: () =>
      import("@/modules/Services/Components/RoleCreation/SLRoleTemplatePopup.vue"),
  },
})
export default class VEmployerCandidateInvite extends Vue {
  loading = false;

  form = FORM_MODEL;

  ICONS = ICONS;

  errorMessage = `The SOC code you selected can only be used for individuals who got CoS for the first Skilled Worker visa before July 22nd, 2025 and have continually held one or more Skilled Worker visas since then. For further instructions and guidance, please follow this <a href="https://compliance.nationbetter.uk/#/expert/guide/104" target="_blank" style="text-decoration: underline; font-weight: 600;">guide</a>.`;

  isEmailAlreadyRegistered = false;

  get emailValid() {
    return REs.email.test(this.form.candidate_email);
  }

  get email() {
    return new CFormField("Email")
      .addRule(() => !this.isEmailAlreadyRegistered || this.$tc("error.emailAlreadyExist"))
      .addRule(() => this.emailValid || this.$tc("error.emailError"));
  }

  @Watch("form.candidate_email")
  onUpdateCandidateEmail() {
    this.isEmailAlreadyRegistered = false;
  }

  get detailsItems() {
    return this.$t("details.list") ?? [];
  }

  get inviteToService() {
    const query = this.$route.query;

    if (!query) return "";

    return query["service"] ?? "";
  }

  checkService() {
    if (this.inviteToService) return;

    this.$router.push(REmployerHomeNew);
  }

  jobTemplates = [];

  async fetchJobTemplates() {
    const params = {
      archive: false,
    };

    const { data } = await this.axios.get(API_ROUTES.ROLE_TEMPLATES(), { params });

    if (!data) return;

    const onlyCompleted = data.filter((item) => item.completed === true);

    this.jobTemplates = onlyCompleted.map((item: API_TRoleTemplate) =>
      convertJobTemplatesToSelectCaseCreation(item)
    );
  }

  mounted() {
    this.fetchJobTemplates();
  }

  get needsUpdate() {
    return SMQuestionnaire.needsUpdate;
  }

  @Watch("needsUpdate")
  update(val: boolean) {
    if (!val) return;

    this.fetchJobTemplates();
  }

  get sendInvitationEnable() {
    const { form, loading, inviteToService, isEmailAlreadyRegistered, emailValid } = this;

    return (
      !someValueInFormIsEmpty(form, []) &&
      !loading &&
      inviteToService &&
      !isEmailAlreadyRegistered &&
      emailValid
    );
  }

  get inputDisabled() {
    return !this.inviteToService;
  }

  showCandidateInvitePopUp = false;

  closeCandidateInvitePopUp() {
    this.showCandidateInvitePopUp = false;

    this.goToCase();
  }

  cleanForm() {
    Object.keys(this.form).forEach((key) => {
      this.form[key] = "";
    });
  }

  caseId = null;

  async goToCase() {
    const { caseId } = this;

    if (!caseId) return;

    await this.$router.push(`/cases/${caseId}/dashboard/${SMCases.tabId}`);
  }

  prepareSendData(payload) {
    return encodeUrlParams(({ ...payload } as unknown) as URLSearchParams);
  }

  async sendInvitation() {
    try {
      this.loading = true;

      const { form, inviteToService } = this;

      if (!inviteToService) return;

      const params = this.prepareSendData(form);

      const { data } = await this.axios.get<TCandidateInvite>(
        API_ROUTES.CASE_CREATE(inviteToService as string, params)
      );

      if (!data) return;

      this.caseId = data.CaseID || 0;

      this.showCandidateInvitePopUp = true;

      // await this.$nextTick();

      // this.cleanForm();

      // this.goToCase();
    } catch (error) {
      this.isEmailAlreadyRegistered = true;
    } finally {
      this.loading = false;
    }
  }

  async createRoleTemplate() {
    // const selectElement = this.$refs.jobTemplateSelect as Vue;

    // if (selectElement) {
    //   selectElement.$emit("close-options");
    //   selectElement?.["closeOptions"]?.();
    // }

    await SMRoles.createRoleTemplate();
  }

  get showWarningMessage(): boolean {
    const selectedJobTemplate = this.jobTemplates.find(
      (i) => i.value === this.form.role_template_id
    );

    if (!selectedJobTemplate) return false;

    return !selectedJobTemplate.soc_code_eligible_for_outside;
  }

  slRoleTemplatePopup = false;

  openSlRoleTemplatePopup() {
    this.slRoleTemplatePopup = true;
  }

  closeSLRoleTemplatePopup() {
    this.slRoleTemplatePopup = false;
  }

  get hasJobTemplates(): boolean {
    return !!this.jobTemplates.length;
  }

  get canImportJobTemplateFromSL(): boolean {
    return SMUser.isSLStarted && !this.hasJobTemplates;
  }

  async addNewJobPosition() {
    await this.createRoleTemplate();
  }

  async beforeRouteEnter(to: any, from: any, next: any) {
    await next((vm: any) => {
      vm.cleanForm();

      setTimeout(() => {
        vm.checkService();
      }, 300);
    });
  }

  async beforeRouteLeave(to: any, from: any, next: any) {
    next(async (vm: any) => {
      vm.cleanForm();
    });
  }
}
